import { useEffect, useState } from 'react';
import { ApiFout, api } from '@/lib/api';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Loader2, Download, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';

const LANGUAGES: Array<{ code: string; label: string; native: string }> = [
  { code: 'Nederlands', label: 'Nederlands', native: 'Nederlands' },
  { code: 'English', label: 'Engels', native: 'English' },
  { code: 'Deutsch', label: 'Duits', native: 'Deutsch' },
  { code: 'Français', label: 'Frans', native: 'Français' },
  { code: 'Español', label: 'Spaans', native: 'Español' },
  { code: 'Italiano', label: 'Italiaans', native: 'Italiano' },
  { code: 'Português', label: 'Portugees', native: 'Português' },
  { code: 'Polski', label: 'Pools', native: 'Polski' },
  { code: 'Українська', label: 'Oekraïens', native: 'Українська' },
  { code: 'Русский', label: 'Russisch', native: 'Русский' },
  { code: 'Türkçe', label: 'Turks', native: 'Türkçe' },
  { code: 'العربية', label: 'Arabisch', native: 'العربية' },
  { code: 'فارسی', label: 'Perzisch', native: 'فارسی' },
  { code: 'Soomaali', label: 'Somalisch', native: 'Soomaali' },
  { code: 'Tiếng Việt', label: 'Vietnamees', native: 'Tiếng Việt' },
  { code: '中文', label: 'Chinees', native: '中文' },
  { code: '日本語', label: 'Japans', native: '日本語' },
  { code: 'हिन्दी', label: 'Hindi', native: 'हिन्दी' },
  { code: 'Bahasa Indonesia', label: 'Indonesisch', native: 'Bahasa Indonesia' },
  { code: 'Tagalog', label: 'Filipijns', native: 'Tagalog' },
];

export default function PatientSharePage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ practice_name: string; consult_at: string } | null>(null);
  const [selected, setSelected] = useState<string>('Nederlands');
  const [downloading, setDownloading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;

    void (async () => {
      try {
        // Publiek, op het token. De anon-key die hier stond bestaat niet meer -- die
        // gaf toegang tot elke edge function en zat in de bundel van iedere bezoeker.
        const antwoord = await api.get<{
          consultAt: string;
          practiceName: string;
          usesLeft: number;
        }>(`/patient-share/${encodeURIComponent(token)}`);

        setMeta({ practice_name: antwoord.practiceName, consult_at: antwoord.consultAt });
      } catch (fout) {
        setError(fout instanceof ApiFout ? fout.message : 'Verbindingsfout. Probeer opnieuw.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleDownload = async () => {
    if (!token || !selected) return;
    setDownloading(true);
    setError(null);
    try {
      /**
       * De PDF komt van de server, als blob.
       *
       * De taal gaat als query-parameter mee omdat dit een GET is: het opvragen van een
       * document, niet het aanmaken ervan. De route telt het gebruik van de link af --
       * twee keer, één voor het bekijken en één voor het downloaden.
       */
      const blob = await api.blob(`/patient-share/${encodeURIComponent(token)}/pdf`, {
        query: { language: selected },
      });

      const a = document.createElement('a');
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = 'consult-samenvatting.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      setDone(true);
    } catch (fout) {
      // De route geeft pdf_taal_niet_ondersteund als er geen Unicode-font is ingesteld
      // en de gekozen taal buiten WinAnsi valt; die melding zegt wat er aan de hand is.
      setError(fout instanceof ApiFout ? fout.message : 'Verbindingsfout bij downloaden.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
          <h1 className="text-xl font-semibold">Link niet geldig</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <CheckCircle2 className="h-14 w-14 text-primary mx-auto" />
          <h1 className="text-2xl font-semibold">PDF gedownload</h1>
          <p className="text-muted-foreground">
            Uw samenvatting is gedownload. Deze link is nu verlopen en kan niet opnieuw worden gebruikt.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 flex justify-center">
      <div className="w-full max-w-md flex flex-col gap-6 py-6">
        <header className="space-y-2 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 mb-2">
            <FileText className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold leading-tight">{meta?.practice_name || 'Uw consultsamenvatting'}</h1>
          {meta?.consult_at && (
            <p className="text-sm text-muted-foreground">
              Consult van {new Date(meta.consult_at).toLocaleString('nl-NL', { dateStyle: 'long', timeStyle: 'short' })}
            </p>
          )}
        </header>

        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Kies uw taal</h2>
          <p className="text-sm text-muted-foreground">
            Choose your language — Wählen Sie Ihre Sprache — Choisissez votre langue — اختر لغتك
          </p>
          <div className="border border-border rounded-lg overflow-hidden bg-card">
            <div className="max-h-[50vh] overflow-y-auto divide-y divide-border">
              {LANGUAGES.map((lang) => {
                const isActive = selected === lang.code;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => setSelected(lang.code)}
                    className={`w-full text-left px-4 py-4 flex items-center justify-between transition-colors ${
                      isActive ? 'bg-primary/10' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div>
                      <div className="text-base font-medium">{lang.native}</div>
                      <div className="text-xs text-muted-foreground">{lang.label}</div>
                    </div>
                    {isActive && <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <Button
          size="lg"
          className="w-full h-14 text-base"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              PDF wordt gemaakt...
            </>
          ) : (
            <>
              <Download className="h-5 w-5 mr-2" />
              Download PDF
            </>
          )}
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          Deze link kan slechts één keer gebruikt worden en verloopt automatisch.
        </p>
      </div>
    </div>
  );
}